import React, { useEffect, useState } from "react";
import { collection, getDocs, Timestamp } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

type Organization = {
  id: string;
  name?: string;
  createdAt?: Timestamp | { seconds?: number; toDate?: () => Date } | null;
  status?: string;
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState({
    organizations: 0,
    users: 0,
    admins: 0,
  });
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [monthlyData, setMonthlyData] = useState<{ month: string; Active: number; Trial: number; Inactive: number; total: number }[]>([]);
  const [statusData, setStatusData] = useState<{ name: string; value: number; color: string }[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all three collections in parallel
        const [orgsSnapshot, usersSnapshot, adminsSnapshot] = await Promise.all([
          getDocs(collection(db, "organization")),
          getDocs(collection(db, "users")),
          getDocs(collection(db, "admins")),
        ]);

        const orgsData: Organization[] = orgsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as any),
        }));

        setOrganizations(orgsData);

        // Set Platform Overview stats to show ALL data (not filtered by year)
        setStats({
          organizations: orgsData.length,
          users: usersSnapshot.docs.length,
          admins: adminsSnapshot.docs.length,
        });

        // Filter data by selected year for charts
        const currentYear = selectedYear;
        
        // Filter organizations created in the selected year (for charts only)
        const orgsInYear = orgsData.filter((org) => {
          let createdDate: Date | null = null;
          if (org.createdAt) {
            if (typeof (org.createdAt as any).toDate === "function") {
              createdDate = (org.createdAt as any).toDate();
            } else if ((org.createdAt as any).seconds) {
              createdDate = new Date(((org.createdAt as any).seconds as number) * 1000);
            }
          }
          return createdDate && createdDate.getFullYear() === currentYear;
        });

        // Fetch subscription data from subscriptions collection first
        const subscriptionsSnapshot = await getDocs(collection(db, "subscriptions"));
        const subscriptionsData = subscriptionsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as any),
        }));

        // Create a map of orgId to subscription status
        const subscriptionMap = new Map<string, string>();
        subscriptionsData.forEach((subscription) => {
          subscriptionMap.set(subscription.orgId, subscription.status || "Inactive");
        });

        // Process monthly data for selected year with subscription status breakdown
        const monthNames = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];

        // Initialize all months with 0 for each status
        const monthlyCounts: { [key: string]: { Active: number; Trial: number; Inactive: number; total: number } } = {};
        monthNames.forEach(month => {
          monthlyCounts[month] = { Active: 0, Trial: 0, Inactive: 0, total: 0 };
        });

        // Count organizations by month and subscription status (using already filtered orgsInYear)
        orgsInYear.forEach((org) => {
          let createdDate: Date | null = null;
          
          if (org.createdAt) {
            if (typeof (org.createdAt as any).toDate === "function") {
              createdDate = (org.createdAt as any).toDate();
            } else if ((org.createdAt as any).seconds) {
              createdDate = new Date(((org.createdAt as any).seconds as number) * 1000);
            }
          }

          if (createdDate) {
            const monthIndex = createdDate.getMonth();
            const month = monthNames[monthIndex];
            const status = subscriptionMap.get(org.id) || "Inactive";
            
            monthlyCounts[month].total++;
            if (status === "Active") {
              monthlyCounts[month].Active++;
            } else if (status === "Trial") {
              monthlyCounts[month].Trial++;
            } else {
              monthlyCounts[month].Inactive++;
            }
          }
        });

        // Convert to array format for chart
        const monthlyChartData = monthNames.map(month => ({
          month,
          Active: monthlyCounts[month].Active,
          Trial: monthlyCounts[month].Trial,
          Inactive: monthlyCounts[month].Inactive,
          total: monthlyCounts[month].total,
        }));

        setMonthlyData(monthlyChartData);

        // Count subscription statuses for organizations created in the selected year
        let activeCount = 0;
        let inactiveCount = 0;
        let trialCount = 0;

        // Only count subscriptions for organizations created in the selected year
        orgsInYear.forEach((org) => {
          const status = subscriptionMap.get(org.id) || "Inactive";
          if (status === "Active") {
            activeCount++;
          } else if (status === "Trial") {
            trialCount++;
          } else {
            inactiveCount++;
          }
        });

        setStatusData([
          { name: "Active", value: activeCount, color: "#10B981" },
          { name: "Trial", value: trialCount, color: "#FBD282" },
          { name: "Inactive", value: inactiveCount, color: "#6B7280" },
        ]);

      } catch (err: any) {
        setError(err?.message || "Failed to load statistics");
        console.error("Error fetching stats:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [selectedYear]);

  const StatCard: React.FC<{
    title: string;
    value: number | string;
    icon: React.ReactNode;
    gradient: string;
    loading?: boolean;
  }> = ({ title, value, icon, gradient, loading: cardLoading }) => (
    <div className="relative rounded-xl border border-[#2E2F33] bg-[#0F1014] p-6 overflow-hidden group hover:border-[#B59143]/50 transition-all duration-300">
      {/* Gradient background effect */}
      <div
        className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-20 group-hover:opacity-30 transition-opacity ${gradient}`}
      />
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-lg ${gradient} bg-opacity-20`}>
            {icon}
          </div>
        </div>
        <p className="text-sm text-[#C3C9D3] mb-2 font-medium">{title}</p>
        {cardLoading ? (
          <div className="h-10 w-20 bg-[#1A1C1F] rounded animate-pulse" />
        ) : (
          <p className="text-4xl font-bold text-white">{value.toLocaleString()}</p>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#2E2F33] border-t-[#B59143] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#C3C9D3]">Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 rounded-xl bg-red-950/30 border border-red-900 text-red-200">
        <p className="font-semibold mb-2">Error loading statistics</p>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Platform Overview - Top */}
      <div className="rounded-xl border border-[#2E2F33] bg-[#0F1014] p-6">
        <h3 className="text-lg font-semibold text-[#E8C16C] mb-4">Platform Overview</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg bg-[#1A1C1F] border border-[#2E2F33]">
            <p className="text-sm text-[#C3C9D3] mb-1">Organizations</p>
            <p className="text-2xl font-bold text-white">{stats.organizations}</p>
            <p className="text-xs text-[#C3C9D3] mt-1">Total organizations</p>
          </div>
          <div className="p-4 rounded-lg bg-[#1A1C1F] border border-[#2E2F33]">
            <p className="text-sm text-[#C3C9D3] mb-1">Users</p>
            <p className="text-2xl font-bold text-white">{stats.users}</p>
            <p className="text-xs text-[#C3C9D3] mt-1">Total users</p>
          </div>
          <div className="p-4 rounded-lg bg-[#1A1C1F] border border-[#2E2F33]">
            <p className="text-sm text-[#C3C9D3] mb-1">Admins</p>
            <p className="text-2xl font-bold text-white">{stats.admins}</p>
            <p className="text-xs text-[#C3C9D3] mt-1">Total admins</p>
          </div>
        </div>
      </div>

      {/* Year Filter - Below Platform Overview */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-[#0F1014] border border-[#2E2F33]">
          <label className="text-sm text-[#C3C9D3] font-medium">Filter by Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-4 py-2 bg-[#1A1C1F] border border-[#2E2F33] text-white rounded-lg focus:border-[#B59143] focus:outline-none text-sm font-semibold"
          >
            {Array.from({ length: 5 }, (_, i) => {
              const year = new Date().getFullYear() - i;
              return (
                <option key={year} value={year}>
                  {year}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Stats Grid */}
     

      {/* Charts Section */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Monthly Organization Trends - Left Side (2/3 width) */}
        <div className="lg:col-span-2 rounded-xl border border-[#2E2F33] bg-[#0F1014] p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#E8C16C] mb-2">Organization Creation Trends</h3>
            <p className="text-sm text-[#C3C9D3]">Monthly organization creation for {selectedYear}</p>
          </div>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2E2F33" />
              <XAxis
                dataKey="month"
                stroke="#C3C9D3"
                style={{ fontSize: "12px" }}
                tick={{ fill: "#C3C9D3" }}
              />
              <YAxis
                stroke="#C3C9D3"
                style={{ fontSize: "12px" }}
                tick={{ fill: "#C3C9D3" }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1A1C1F",
                  border: "1px solid #2E2F33",
                  borderRadius: "8px",
                  color: "#C3C9D3",
                }}
                labelStyle={{ color: "#E8C16C" }}
              />
              <Bar dataKey="Active" stackId="a" fill="#10B981" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Trial" stackId="a" fill="#FBD282" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Inactive" stackId="a" fill="#6B7280" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          
          {/* Legend */}
          <div className="flex items-center justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: "#10B981" }} />
              <span className="text-sm text-[#C3C9D3]">Active</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: "#FBD282" }} />
              <span className="text-sm text-[#C3C9D3]">Trial</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: "#6B7280" }} />
              <span className="text-sm text-[#C3C9D3]">Inactive</span>
            </div>
          </div>
        </div>

        {/* Subscription Status Breakdown - Right Side (1/3 width) */}
        <div className="rounded-xl border border-[#2E2F33] bg-[#0F1014] p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#E8C16C] mb-2">Subscription Status</h3>
            <p className="text-sm text-[#C3C9D3]">Active, Trial & Inactive breakdown</p>
          </div>
          
          {/* Status Counts */}
          <div className="mb-4 space-y-3">
            {statusData.map((status) => (
              <div key={status.name} className="flex items-center justify-between p-3 rounded-lg bg-[#1A1C1F] border border-[#2E2F33]">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: status.color }}
                  />
                  <span className="text-sm text-[#C3C9D3] font-medium">{status.name}</span>
                </div>
                <span className="text-lg font-bold text-white">{status.value}</span>
              </div>
            ))}
          </div>
          
          {/* Pie Chart */}
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1A1C1F",
                  border: "1px solid #2E2F33",
                  borderRadius: "8px",
                  color: "#C3C9D3",
                }}
                formatter={(value: number) => [value, "Subscriptions"]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;


